import QR from "qrcode";
import { abrirAppleWallet } from "@panel/apple-wallet";
import { ErrorDelEdge } from "@panel/edge";

import { sesionActual } from "@panel/pase-modelo";
import type {
  PaseDelColaborador,
  PuertoDelPase,
  SesionDelPase,
} from "@panel/pase-modelo";

/**
 * TAP — el transporte del pase: HTTP contra el edge, y el dibujante del QR.
 *
 * `shared/panel` declara la INTERFAZ (`PuertoDelPase`) y esta app la implementa.
 * Misma doctrina que `PuertoDelPanel`: el panel compartido no sabe de `fetch` ni
 * de `qrcode`, y por eso se puede probar sin navegador.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LA BASE ES RELATIVA, Y ESO ES LO QUE MANTIENE UN SOLO ORIGEN
 * ═══════════════════════════════════════════════════════════════════════════
 * `VITE_POS_API=/api`, igual que el kiosco desde el paso 2. Relativa, la
 * dirección la resuelve el navegador contra el origen del que bajó la página,
 * así que esta app le habla a SU propio `/api` — nunca al de `tap.`.
 *
 * Si algún día esto fuera absoluto, el portal le hablaría a otro nombre, el
 * navegador mandaría un preflight de CORS, y alguien «arreglaría» el 403
 * agregando `Access-Control-Allow-Origin` en el servidor. Ese es el camino que
 * el despliegue existe para no tomar: dos nombres, un solo backend, y cada
 * nombre con un solo origen.
 */
const BASE = (import.meta.env["VITE_POS_API"] ?? "") as string;

/** El error que la pantalla sabe pintar. */


async function pedir<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  let r: Response;
  try {
    r = await fetch(`${BASE}${ruta}`, opciones);
  } catch {
    // Sin red. Se dice así y no «error 0»: la persona puede hacer algo con
    // esto, y con un número no.
    throw new ErrorDelEdge(0, "No hay conexión. Revisa tu internet e inténtalo otra vez.");
  }
  if (!r.ok) {
    // El edge manda `{detail: "..."}` en sus errores. Se prefiere SU texto al
    // nuestro: «tu pase fue revocado el 12 de septiembre» le sirve a la persona
    // mucho más que «403».
    let detalle = "";
    try {
      const cuerpo = (await r.json()) as { detail?: unknown };
      if (typeof cuerpo.detail === "string") detalle = cuerpo.detail;
      else if (cuerpo.detail && typeof cuerpo.detail === "object" && "detail" in cuerpo.detail) detalle = String(cuerpo.detail.detail);
    } catch {
      /* el cuerpo no era JSON; nos quedamos con el código */
    }
    throw new ErrorDelEdge(r.status, detalle || `El servidor contestó ${r.status}.`);
  }
  return (await r.json()) as T;
}

export const puerto: PuertoDelPase = {
  entrar(token: string): Promise<SesionDelPase> {
    // La liga viaja en el CUERPO, no en la URL de esta llamada. Ya estuvo en la
    // barra de direcciones —no hay forma de que una liga no lo esté— pero de
    // ahí en adelante no tiene por qué volver a aparecer en los registros del
    // proxy ni en el historial.
    // El campo se llama `token`, medido contra `EntradaConLiga` en
    // `courtesies.py:76-85`. No `liga`: el modelo del servidor lo rechazaría
    // con un 422 que en pantalla se leería como «la liga no sirve».
    return pedir<SesionDelPase>("/cortesias/pase/entrar", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
  },

  miPase(): Promise<PaseDelColaborador> {
    const sesion = sesionActual();
    // `staff_id` NO va en el query, y esa es la razón de ser de la ruta: el
    // servidor lo saca de la sesión. Si algún día esta llamada lo mandara, un
    // colaborador podría leer el pase de otro.
    return pedir<PaseDelColaborador>("/cortesias/mi-pase", {
      headers: sesion ? { Authorization: `Bearer ${sesion}` } : {},
    });
  },

  async appleWallet(): Promise<void> {
    const sesion = sesionActual();
    const { ticket } = await pedir<{ ticket: string }>("/cortesias/mi-pase/apple-wallet", {
      method: 'POST', headers: sesion ? { Authorization: `Bearer ${sesion}` } : {},
    });
    // La identidad pudo cambiar mientras se preparaba el archivo.
    if (!sesion || sesion !== sesionActual()) throw new Error('La sesión cambió. Abre tu pase otra vez.');
    abrirAppleWallet(`${BASE}/cortesias/wallet/apple.pkpass`, ticket);
  },

  async googleWallet(): Promise<void> {
    const sesion = sesionActual();
    const { url } = await pedir<{ url: string }>("/cortesias/mi-pase/google-wallet", {
      method: 'POST', headers: sesion ? { Authorization: `Bearer ${sesion}` } : {},
    });
    if (!sesion || sesion !== sesionActual()) throw new Error('La sesión cambió. Abre tu pase otra vez.');
    if (!url.startsWith('https://pay.google.com/gp/v/save/') || url.length > 1800) throw new Error('Enlace de Wallet inválido.');
    window.location.assign(url);
  },

  async dibujarQR(texto: string): Promise<string> {
    // SVG y no canvas: escala sin pixelearse en cualquier densidad de pantalla,
    // y el lector de la barra le apunta a un teléfono que puede ser de 2019.
    // Margen 1 (y no el 4 por defecto) porque el respaldo blanco ya lo pone la
    // pantalla; dos márgenes encima achican los módulos sin ganar nada.
    return QR.toString(texto, { type: "svg", margin: 1, errorCorrectionLevel: "M" });
  },
};
